import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppointmentsEntryButton, getTodayIso, isNotCanceled } from '@odo/shared-ui';
import FilterDropdown from '../../components/filtering/FilterDropdown';
import SearchBar from '../../components/navigation/SearchBar';
import AddPatientButton from '../../components/patientsTable/AddPatientButton';
import PatientTable from '../../components/patientsTable/PatientTable';
import StatsBar from '../../components/patientsTable/StatsBar';
import WelcomeBanner from '../../components/WelcomeBanner';
import { useSearch } from '../../contexts/SearchContext';
import { APPOINTMENTS_QUERY_PARAM, APPOINTMENTS_QUERY_OPEN } from '../../utils/constants';
import { useAppointmentsMine } from '../../hooks/useAppointments';
import { PatientListProvider, usePatientList } from '../../providers/patient/PatientsListProvider';
import { usePractice } from '../../providers/practices/PracticesProvider';
import { useUser } from '../../providers/UserProvider';

// The appointments modal itself is mounted globally now (see
// GlobalAppointmentsModal, rendered from PatientActionsContext) so it can be
// opened from any page - this page only owns the entry-button click that
// sets `?appointments=open`, the same push-on-open pattern as billing's
// InvoicesSection (`INVOICE_QUERY_PARAM`): opening pushes a new history
// entry, so Back closes the modal again. Which appointment (if any) is open
// in the composer, and closing everything back out, are both owned by
// GlobalAppointmentsModal / AppointmentsModal (see APPOINTMENT_QUERY_PARAM
// there). APPOINTMENTS_QUERY_PARAM / APPOINTMENTS_QUERY_OPEN live in
// utils/constants so components rendered inside this page (the patients
// table) can open the modal without importing this page.

const PatientsOverviewContent: React.FC = () => {
    const navigate = useNavigate();
    const {
        treatmentStatusFilter,
        setTreatmentStatusFilter,
        actionRequiredFilter,
        setActionRequiredFilter,
        practiceFilter,
        setPracticeFilter
    } = useSearch();
    const { user } = useUser();
    const { practices } = usePractice();
    const { patients } = usePatientList();
    const { appointments } = useAppointmentsMine();
    const [, setSearchParams] = useSearchParams();

    const handleOpenAppointments = () => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(APPOINTMENTS_QUERY_PARAM, APPOINTMENTS_QUERY_OPEN);
            return next;
        });
    };

    const upcomingAppointmentsCount = useMemo(() => {
        const todayIso = getTodayIso();
        return appointments.filter((a) => isNotCanceled(a) && a.date >= todayIso).length;
    }, [appointments]);

    const hasPracticeIds = user?.database?.data?.practices?.length ?? 0;
    const hasMultiplePractices = practices.length > 1;

    const practiceOptions = useMemo(
        () => practices.map((p) => p.name || 'Unknown').filter(Boolean) as string[],
        [practices]
    );

    const treatmentStatusOptions = useMemo(() => {
        const statuses = new Set<string>();
        patients.forEach((patient) => {
            const status = patient.treatmentStatus;
            if (status) {
                statuses.add(status);
            }
        });
        return Array.from(statuses).sort();
    }, [patients]);

    const actionRequiredOptions = useMemo(() => {
        const actions = new Set<string>();
        patients.forEach((patient) => {
            const action = patient.requiredAction;
            if (action && action !== 'None') {
                actions.add(action);
            } else {
                actions.add('No action needed');
            }
        });
        return Array.from(actions).sort();
    }, [patients]);

    return (
        <div className="flex flex-col h-full w-full">
            {/* Top left welcome message */}
            <WelcomeBanner />

            {/* Stats overview cards */}
            <div className="mt-24">
                <StatsBar patients={patients} />
            </div>

            {/* Top bar with search and add button */}
            <div className="flex items-center justify-between w-full px-8 mt-5">
                <div className="flex items-center gap-4">
                    <SearchBar placeholder="Search by first or last name" />
                </div>
                <div className="flex items-center gap-4">
                    {hasMultiplePractices && (
                        <FilterDropdown
                            title="Practice"
                            options={practiceOptions}
                            selectedOptions={practiceFilter}
                            onChange={setPracticeFilter}
                        />
                    )}
                    <FilterDropdown
                        title="Treatment Status"
                        options={treatmentStatusOptions}
                        selectedOptions={treatmentStatusFilter}
                        onChange={setTreatmentStatusFilter}
                    />
                    <FilterDropdown
                        title="Action Required"
                        options={actionRequiredOptions}
                        selectedOptions={actionRequiredFilter}
                        onChange={setActionRequiredFilter}
                    />
                    <AppointmentsEntryButton
                        upcomingCount={upcomingAppointmentsCount}
                        onClick={handleOpenAppointments}
                    />
                    <AddPatientButton
                        onClick={() => navigate('/patient/creation')}
                        isDisabled={!hasPracticeIds}
                    />
                </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col w-full">
                <PatientTable />
            </div>
        </div>
    );
};

const PatientsOverview: React.FC = () => {
    return (
        <PatientListProvider>
            <PatientsOverviewContent />
        </PatientListProvider>
    );
};

export default PatientsOverview;
